import Configuration from '@/api/Configuration';
import {
  toCreatePromotionRequest,
  toPromotion,
  toPromotions,
  toUpdatePromotionRequest,
} from '@/api/mappers/mapPromotion';
import {
  PromotionsApi as ApiPromotionsApi,
  CreatePromotionRequest,
  DeletePromotionRequest,
  GetPromotionRequest,
  ListPromotionsRequest,
  UpdatePromotionRequest,
} from '@/gen/api/apis/PromotionsApi';
import { Promotion, PromotionCreate, Promotions, PromotionUpdate } from '@/models/Promotion';

export interface PromotionFilters {
  supplierId?: string | string[];
}

interface PromotionsApi {
  listPromotions(offset?: number, limit?: number): Promise<Promotions>;
  findPromotions(
    query?: string,
    filters?: PromotionFilters,
    offset?: number,
    limit?: number,
  ): Promise<Promotions>;
  getPromotion(id: string): Promise<Promotion | undefined>;
  createPromotion(promotion: PromotionCreate): Promise<Promotion | undefined>;
  updatePromotion(id: string, promotion: Promotion): Promise<Promotion | undefined>;
  deletePromotion(id: string): Promise<void>;
}

export class PromotionsApiImpl implements PromotionsApi {
  private promotionsApi: ApiPromotionsApi;

  constructor(configuration: Configuration) {
    this.promotionsApi = new ApiPromotionsApi(configuration);
  }

  async getPromotion(id: string): Promise<Promotion | undefined> {
    const request: GetPromotionRequest = {
      id,
    };

    try {
      const response = await this.promotionsApi.getPromotion(request);
      return toPromotion(response);
    } catch (error) {
      console.error('Error getting promotion:', error);
      return;
    }
  }

  async listPromotions(offset?: number, limit?: number): Promise<Promotions> {
    const request: ListPromotionsRequest = {
      offset,
      limit,
    };

    try {
      const response = await this.promotionsApi.listPromotions(request);
      return toPromotions(response);
    } catch (error) {
      console.error('Error listing promotions:', error);
      throw error;
    }
  }

  async findPromotions(
    query?: string,
    filters?: PromotionFilters,
    offset?: number,
    limit?: number,
  ): Promise<Promotions> {
    const request: ListPromotionsRequest = {
      query,
      offset,
      limit,
      supplierId: filters?.supplierId
        ? Array.isArray(filters?.supplierId)
          ? filters?.supplierId
          : [filters?.supplierId]
        : undefined,
    };

    try {
      const response = await this.promotionsApi.listPromotions(request);
      return toPromotions(response);
    } catch (error) {
      console.error('Error finding promotions:', error);
      throw error;
    }
  }

  async createPromotion(promotion: PromotionCreate): Promise<Promotion | undefined> {
    const request: CreatePromotionRequest = {
      promotionCreateRequest: toCreatePromotionRequest(promotion),
    };

    try {
      const result = await this.promotionsApi.createPromotion(request);
      return toPromotion(result);
    } catch (error) {
      console.error('Error creating promotion:', error);
      throw error;
    }
  }

  async updatePromotion(id: string, promotion: PromotionUpdate): Promise<Promotion | undefined> {
    const request: UpdatePromotionRequest = {
      id,
      promotionUpdateRequest: toUpdatePromotionRequest(promotion),
    };

    try {
      const result = await this.promotionsApi.updatePromotion(request);
      return toPromotion(result);
    } catch (error) {
      console.error('Error updating promotion:', error);
      throw error;
    }
  }

  async deletePromotion(id: string): Promise<void> {
    const request: DeletePromotionRequest = { id };

    try {
      await this.promotionsApi.deletePromotion(request);
    } catch (error) {
      console.error('Error deleting promotion:', error);
      throw error;
    }
  }
}

export default PromotionsApi;
